<template>
  <NuxtLink
    ref="brochureItem"
    class="brochure-item"
    :to="
      localePath({
        name: 'slug-offers-id-pagenumber',
        params: { slug: brochure.shop.slug, id: brochure.id, pagenumber: '0' },
      })
    "
    @click="handleBrochureClick"
  >
    <div class="brochure-item-cover">
      <BaseImageExternal
        v-if="brochure.cover.fileUrl"
        class="brochure-cover"
        :src="brochure.cover.fileUrl"
        :alt="$t('brochure.cover-alt', [brochure.name])"
        :aspect-ratio="brochure.cover.imageRatio"
        :fetchpriority="fetchpriority"
        :lazy="lazy"
      />
    </div>
    <div class="brochure-item-info">
      <BaseText class="card-title">{{ brochure.name }}</BaseText>
      <BrochureDate
        :expire-after="brochure.expireAfter"
        :active-from="brochure.activeFrom"
        class="card-subtitle"
      />
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import type {
  BrochureFragment,
  BrochureSearchResultFragment,
  BrochureImpressionSource,
} from "~/graphql/generated";
import { useCreateBrochureImpressionStatisticMutation } from "~/graphql/generated";

const props = defineProps<{
  brochure: BrochureFragment | BrochureSearchResultFragment;
  impressionSource: BrochureImpressionSource;
  /** Set the fetchpriority on the brochure's image */
  fetchpriority?: "high" | "auto";
  /** Should the brochure's image lazy load? */
  lazy?: boolean;
  /** An optional payload to sent to GTM when clicking a brochure. */
  eventSource?: string;
}>();

const localePath = useLocalePath();

// Statistics mutations
const createBrochureImpression = useCreateBrochureImpressionStatisticMutation();

// Log the impression when the item is visible
const brochureItem = ref<HTMLAnchorElement>();
const isBrochureItemVisible = useElementVisibility(brochureItem);
const deviceId = useDeviceId();

watch(
  isBrochureItemVisible,
  (isVisible) => {
    if (isVisible) {
      createBrochureImpression.executeMutation({
        createBrochureImpressionStatistic: {
          brochureId: props.brochure.id,
          source: props.impressionSource,
          deviceId: deviceId.value,
        },
      });
    }
  },
  { immediate: true },
);

// Set the impression state, that we use for the brochure visit statistic in the brochure viewer
const { setVisitSource } = useVisitSource();

/**
 * We sometimes need to sent an event to GTM so we have a special click handler when clicking a brochure.
 * Currently also always passes the shop as an extra parameter as well.
 */
function handleBrochureClick() {
  setVisitSource(props.impressionSource);
}
</script>

<style lang="scss" scoped>
.brochure-item {
  max-width: 520px;
  background-color: $light-95;
  border: 1px solid $light-80;
  overflow: hidden;
  border-radius: $border-radius;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .brochure-item-cover {
    flex: 1;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color $easing;
    min-height: 395px; /* So the google in-feed ad knows what size to fill */

    @include for-extra-small-screen {
      min-height: 200px;
    }
  }

  &:hover .brochure-item-cover {
    background-color: $light-80;
  }

  .brochure-item-info {
    background-color: $light-100;
    margin-top: auto;
    padding: 0.5rem;

    .card-title {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
